import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiSsoExchange, apiSsoExchangeUser } from '@/lib/api';

/**
 * SSO-Einstieg aus GoHighLevel (Magic Link / Custom Tab).
 * Der Token steht im URL-FRAGMENT (#t=...), damit er NICHT in Server-/Proxy-Logs
 * landet (Fragmente werden nicht an den Server gesendet). Kontakt-Parameter
 * (nicht geheim) duerfen im Hash oder Query stehen.
 * Tauscht den Magic-Token gegen ein JWT, setzt die Session (localStorage, iframe-
 * tauglich) und leitet in den vorausgefuellten Kalkulator weiter.
 */

// Kontakt-Parameter, die wir an den Kalkulator durchreichen
const PASS_THROUGH = ['cid', 'company', 'name', 'email', 'phone', 'plz', 'verbrauch', 'preis', 'supplier', 'branche'];

// Params aus Hash UND Query mergen (Hash hat Vorrang)
const collectParams = () => {
  const merged = new URLSearchParams(window.location.search);
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) {
    const hp = new URLSearchParams(hash);
    hp.forEach((v, k) => merged.set(k, v));
  }
  return merged;
};

const SsoGhl = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode-Doppelausfuehrung verhindern
    ran.current = true;

    const params = collectParams();
    const email = params.get('u');
    const secret = params.get('k');
    const token = params.get('t') || params.get('token');

    let exchange;
    if (email && secret && !email.includes('{{') && !secret.includes('{{')) {
      // User-basiertes SSO (geteilter Link mit {{user.email}})
      exchange = apiSsoExchangeUser(email.trim(), secret.trim(), params.get('name') || '');
    } else if (token && !token.includes('{{')) {
      // Fallback: fester Magic-Token pro User
      exchange = apiSsoExchange(token.trim());
    } else {
      setError('Kein gueltiger SSO-Parameter uebergeben.');
      return;
    }

    exchange.then((res) => {
      if (!res.ok) {
        setError(res.error || 'Anmeldung fehlgeschlagen.');
        return;
      }
      // Kontaktdaten aus GHL an den Kalkulator weiterreichen (Query, nicht geheim)
      const q = new URLSearchParams();
      PASS_THROUGH.forEach((k) => {
        const v = params.get(k);
        if (v && v.trim() && !v.includes('{{')) q.set(k, v.trim());
      });
      const qs = q.toString();
      navigate(`/app/kalkulator${qs ? `?${qs}` : ''}`, { replace: true });
    });
  }, [navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-6 text-center">
      {!error ? (
        <>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-5" />
          <p className="text-gray-700 font-medium">Anmeldung laeuft …</p>
          <p className="text-gray-400 text-sm mt-1">Sie werden direkt in den Kalkulator weitergeleitet.</p>
        </>
      ) : (
        <div className="max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4 text-2xl">!</div>
          <p className="text-gray-900 font-semibold mb-1">Automatische Anmeldung fehlgeschlagen</p>
          <p className="text-gray-500 text-sm mb-5">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium"
          >
            Zum Login
          </button>
        </div>
      )}
    </div>
  );
};

export default SsoGhl;
